import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Button,
  Flex,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Center,
  Text,
  IconButton,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import { taxaSelicService } from '../../../../services/authService';
import TaxaModal from './TaxaModal';

const TaxasValores = () => {
  const [taxas, setTaxas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTaxa, setSelectedTaxa] = useState(null);
  const [taxaToDelete, setTaxaToDelete] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const cancelRef = React.useRef();
  const toast = useToast();

  // Fetch taxas on mount
  useEffect(() => {
    fetchTaxas();
  }, []);

  const fetchTaxas = async () => {
    setLoading(true);
    try {
      const response = await taxaSelicService.getAll();
      if (response.success) {
        setTaxas(response.data || []);
      } else {
        toast({
          title: 'Erro',
          description: response.message || 'Erro ao buscar taxas',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching taxas:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao buscar taxas',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTaxa = () => {
    setSelectedTaxa(null);
    onOpen();
  };

  const handleEditTaxa = (taxa) => {
    setSelectedTaxa(taxa);
    onOpen();
  };

  const handleDeleteClick = (taxa) => {
    setTaxaToDelete(taxa);
    onDeleteOpen();
  };

  const handleSaveTaxa = async (data) => {
    try {
      let response;
      if (selectedTaxa) {
        // Update existing taxa
        response = await taxaSelicService.update(selectedTaxa._id, data);
      } else {
        // Create new taxa
        response = await taxaSelicService.create(data);
      }

      if (response.success) {
        toast({
          title: 'Sucesso',
          description:
            response.message ||
            `Taxa ${selectedTaxa ? 'atualizada' : 'criada'} com sucesso`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchTaxas();
        onClose();
      } else {
        toast({
          title: 'Erro',
          description: response.message || 'Erro ao salvar taxa',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error saving taxa:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao salvar taxa',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!taxaToDelete) return;

    try {
      const response = await taxaSelicService.delete(taxaToDelete._id);

      if (response.success) {
        toast({
          title: 'Sucesso',
          description: response.message || 'Taxa deletada com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchTaxas();
      } else {
        toast({
          title: 'Erro',
          description: response.message || 'Erro ao deletar taxa',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error deleting taxa:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao deletar taxa',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setTaxaToDelete(null);
      onDeleteClose();
    }
  };

  // Format taxa for display (0.105 -> 10,5%)
  const formatTaxa = (taxa) => {
    const percentage = (taxa * 100).toFixed(2);
    return percentage.replace('.', ',') + '%';
  };

  return (
    <Box p={{ base: 4, md: 8 }}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8} flexWrap="wrap" gap={4}>
        <Heading size="lg">Taxas Selic/IPCA para Precatórios</Heading>
        <Button
          leftIcon={<MdAdd />}
          colorScheme="purple"
          onClick={handleAddTaxa}
        >
          Nova Taxa
        </Button>
      </Flex>

      {/* Table Card */}
      <Card>
        <CardBody>
          {loading ? (
            <Center py={10}>
              <Spinner size="xl" color="purple.500" />
            </Center>
          ) : taxas.length === 0 ? (
            <Center py={10}>
              <Text color="gray.500">
                Nenhuma taxa cadastrada. Clique em "Nova Taxa" para adicionar.
              </Text>
            </Center>
          ) : (
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Ano</Th>
                    <Th>Taxa</Th>
                    <Th textAlign="right">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {taxas.map((taxa) => (
                    <Tr key={taxa._id}>
                      <Td fontWeight="bold">{taxa.ano}</Td>
                      <Td>{formatTaxa(taxa.taxa)}</Td>
                      <Td textAlign="right">
                        <IconButton
                          aria-label="Editar taxa"
                          icon={<MdEdit />}
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          mr={2}
                          onClick={() => handleEditTaxa(taxa)}
                        />
                        <IconButton
                          aria-label="Deletar taxa"
                          icon={<MdDelete />}
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleDeleteClick(taxa)}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </CardBody>
      </Card>

      {/* Taxa Modal */}
      <TaxaModal
        isOpen={isOpen}
        onClose={onClose}
        taxa={selectedTaxa}
        onSave={handleSaveTaxa}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Deletar Taxa
            </AlertDialogHeader>

            <AlertDialogBody>
              Tem certeza que deseja deletar a taxa do ano{' '}
              <strong>{taxaToDelete?.ano}</strong>? Esta ação não pode ser
              desfeita.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Deletar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TaxasValores;
